export type Role = "ADMIN" | "BOARD_MEMBER" | "RESIDENT" | "PUBLIC";

export interface ScreenDoc {
  path: string;
  module: string;
  titleEs: string;
  titleEn: string;
  purposeEs: string;
  purposeEn: string;
  actionsEs: string[];
  actionsEn: string[];
  tipsEs?: string[];
  tipsEn?: string[];
  roles: Role[];
}

// Single source of truth for "what does this screen do" — used by the
// /help/screens reference page and by the sidebar's per-item info popovers.
export const SCREENS: ScreenDoc[] = [
  {
    path: "/",
    module: "Público",
    titleEs: "Página de Inicio",
    titleEn: "Landing Page",
    purposeEs: "Página pública con información del condominio y los comunicados publicados. Punto de entrada para residentes que aún no han iniciado sesión.",
    purposeEn: "Public page with condominium information and published announcements. Entry point for residents who haven't logged in yet.",
    actionsEs: ["Ver últimos comunicados publicados", "Cambiar idioma (ES/EN)", "Iniciar sesión"],
    actionsEn: ["View latest published announcements", "Switch language (ES/EN)", "Sign in"],
    roles: ["PUBLIC"],
  },
  {
    path: "/login",
    module: "Auth",
    titleEs: "Iniciar Sesión",
    titleEn: "Sign In",
    purposeEs: "Pantalla de autenticación. Use sus credenciales asignadas por la administración.",
    purposeEn: "Authentication screen. Use credentials assigned by administration.",
    actionsEs: ["Iniciar sesión con correo y contraseña", "Solicitar restablecimiento de contraseña"],
    actionsEn: ["Sign in with email and password", "Request password reset"],
    tipsEs: ["Las contraseñas iniciales se envían por correo. Cámbiela en el primer inicio de sesión."],
    tipsEn: ["Initial passwords are sent by email. Change yours on first sign-in."],
    roles: ["PUBLIC"],
  },
  {
    path: "/dashboard",
    module: "Tablero",
    titleEs: "Tablero Principal",
    titleEn: "Main Dashboard",
    purposeEs: "Vista general con métricas clave: reuniones próximas, mantenimientos abiertos, cuotas vencidas, permisos y resoluciones pendientes, y contratos por vencer.",
    purposeEn: "Overview with key metrics: upcoming meetings, open maintenance, overdue fees, pending permits and resolutions, expiring contracts.",
    actionsEs: ["Hacer clic en cualquier tarjeta para ir al módulo correspondiente", "Ver próximas 3 reuniones", "Ver contratos que vencen en menos de 90 días"],
    actionsEn: ["Click any card to jump to the relevant module", "See next 3 meetings", "See contracts expiring within 90 days"],
    roles: ["ADMIN", "BOARD_MEMBER", "RESIDENT"],
  },
  {
    path: "/meetings",
    module: "Gobierno",
    titleEs: "Reuniones",
    titleEn: "Meetings",
    purposeEs: "Lista de todas las reuniones de Junta y Asambleas (pasadas, programadas y completadas).",
    purposeEn: "List of all Board meetings and Assemblies (past, scheduled, completed).",
    actionsEs: ["Crear nueva reunión con agenda", "Filtrar por tipo y estado", "Abrir detalle haciendo clic en el título"],
    actionsEn: ["Create new meeting with agenda", "Filter by type and status", "Open detail by clicking the title"],
    roles: ["ADMIN", "BOARD_MEMBER"],
  },
  {
    path: "/meetings/:id",
    module: "Gobierno",
    titleEs: "Detalle de Reunión",
    titleEn: "Meeting Detail",
    purposeEs: "Información completa de una reunión específica: agenda, acta (borrador/aprobada), comentarios inline y resoluciones generadas.",
    purposeEn: "Full info for a specific meeting: agenda, minutes (draft/approved), inline comments, and generated resolutions.",
    actionsEs: ["Redactar borrador del acta", "Agregar comentarios inline", "Editar contenido final del acta", "Aprobar y distribuir (envía PDF por correo a todos los titulares)"],
    actionsEn: ["Draft minutes", "Add inline comments", "Edit final minutes content", "Approve and distribute (sends PDF by email to all owners)"],
    tipsEs: ["Una vez aprobada, el acta queda bloqueada y se genera un PDF automáticamente."],
    tipsEn: ["Once approved, minutes are locked and a PDF is auto-generated."],
    roles: ["ADMIN", "BOARD_MEMBER"],
  },
  {
    path: "/resolutions",
    module: "Gobierno",
    titleEs: "Resoluciones",
    titleEn: "Resolutions",
    purposeEs: "Resoluciones de la Junta con conteo de votos en vivo, y las solicitudes de obra enviadas por la Administración para aprobar o denegar. Las aprobadas tienen PDF descargable y su copia firmada.",
    purposeEn: "Board resolutions with live vote tally, and the works permits the Administration sends to be approved or denied. Approved ones have a downloadable PDF and their signed copy.",
    actionsEs: ["Crear nueva resolución", "Votar A FAVOR / EN CONTRA / ABSTENCIÓN", "Revisar y decidir una solicitud de obra (Aprobar / Denegar)", "Ver la solicitud y toda su documentación", "Subir la resolución firmada", "Descargar PDF de resolución aprobada"],
    actionsEn: ["Create new resolution", "Vote YES / NO / ABSTAIN", "Review and decide a works permit (Approve / Deny)", "See the request and all its documents", "Upload the signed resolution", "Download approved resolution PDF"],
    tipsEs: ["Solo votan los miembros activos de la Junta; el conteo muestra cuántos de ellos han votado.", "La resolución cambia automáticamente a APROBADA o RECHAZADA cuando todos los miembros activos de la Junta han votado. Si alguien no vota, la Administración puede «Cerrar votación» y se decide con los votos emitidos.", "Una solicitud de obra se decide sin votación: al aprobarla o denegarla, la solicitud queda marcada igual en Solicitudes de Titulares."],
    tipsEn: ["Only active board members vote; the tally shows how many of them have voted.", "The resolution auto-switches to APPROVED or REJECTED once all active Board members have voted. If someone does not vote, the Administration can “Close voting” and it is decided on the votes cast.", "A works permit is decided without a vote: approving or denying it marks the request the same way in Works Permits."],
    roles: ["ADMIN", "BOARD_MEMBER"],
  },
  {
    path: "/finance/dashboard",
    module: "Finanzas",
    titleEs: "Tablero Financiero",
    titleEn: "Finance Dashboard",
    purposeEs: "KPIs financieros con gráficos: total facturado, total cobrado, vencido, tasa de cobro, gastos del año, tendencia mensual y presupuesto vs real.",
    purposeEn: "Financial KPIs with charts: total billed, collected, overdue, collection rate, YTD expenses, monthly trend, budget vs actual.",
    actionsEs: ["Ver tasa de cobro como gráfico de dona", "Comparar 6 meses de facturación vs cobro", "Identificar categorías que exceden el presupuesto (rojo)"],
    actionsEn: ["See collection rate as donut chart", "Compare 6 months billed vs collected", "Spot categories over budget (red bars)"],
    roles: ["ADMIN", "BOARD_MEMBER"],
  },
  {
    path: "/finance/fees",
    module: "Finanzas",
    titleEs: "Cuotas de Mantenimiento",
    titleEn: "Maintenance Fees",
    purposeEs: "Tabla unidad por unidad de cuotas mensuales. Genere cuotas en masa, marque pagos y envíe recordatorios.",
    purposeEn: "Unit-by-unit table of monthly fees. Bulk-generate, mark paid, and send reminders.",
    actionsEs: ["Generar cuotas del mes para todas las unidades (un clic)", "Marcar cuota como pagada con método y referencia", "Enviar recordatorio individual"],
    actionsEn: ["Generate monthly fees for all units (one click)", "Mark fee as paid with method and reference", "Send individual reminder"],
    tipsEs: ["El sistema envía recordatorios automáticos a unidades con saldo pendiente; los días, la hora y la plantilla se configuran en Finanzas → Recordatorios."],
    tipsEn: ["The system sends automatic reminders to units with outstanding balances; the days, hour and template are set in Finance → Reminders."],
    roles: ["ADMIN", "BOARD_MEMBER"],
  },
  {
    path: "/finance/expenses",
    module: "Finanzas",
    titleEs: "Gastos",
    titleEn: "Expenses",
    purposeEs: "Cola de aprobación de gastos. Cada gasto pasa por PENDIENTE → APROBADO → PAGADO.",
    purposeEn: "Expense approval queue. Each expense flows PENDING → APPROVED → PAID.",
    actionsEs: ["Registrar nuevo gasto con categoría y proveedor", "Aprobar o rechazar gastos pendientes", "Marcar gasto aprobado como pagado"],
    actionsEn: ["Record new expense with category and vendor", "Approve or reject pending expenses", "Mark approved expense as paid"],
    roles: ["ADMIN", "BOARD_MEMBER"],
  },
  {
    path: "/finance/reports",
    module: "Finanzas",
    titleEs: "Informes Financieros",
    titleEn: "Financial Reports",
    purposeEs: "Repositorio de informes mensuales, anuales y de presupuesto vs real subidos por la Tesorera.",
    purposeEn: "Repository of monthly, annual, and budget-vs-actual reports uploaded by the Treasurer.",
    actionsEs: ["Subir nuevo informe (URL de Cloudinary o Drive)", "Filtrar por año y tipo", "Abrir informe en pestaña nueva"],
    actionsEn: ["Upload new report (Cloudinary or Drive URL)", "Filter by year and type", "Open report in new tab"],
    roles: ["ADMIN", "BOARD_MEMBER"],
  },
  {
    path: "/finance/budgets",
    module: "Finanzas",
    titleEs: "Presupuestos",
    titleEn: "Budgets",
    purposeEs: "Presupuesto anual del condominio por categoría, con aprobación formal.",
    purposeEn: "The condominium's annual budget by category, with formal approval.",
    actionsEs: ["Crear presupuesto para un año fiscal", "Agregar categorías con monto asignado", "Aprobar el presupuesto"],
    actionsEn: ["Create a budget for a fiscal year", "Add categories with allocated amounts", "Approve the budget"],
    roles: ["ADMIN", "BOARD_MEMBER"],
  },
  {
    path: "/finance/close",
    module: "Finanzas",
    titleEs: "Cierre Mensual",
    titleEn: "Monthly Close",
    purposeEs: "Resumen financiero del mes (ingresos, gastos, fondo de reserva) que se publica para los residentes.",
    purposeEn: "The month's financial summary (income, expenses, reserve fund) published for residents.",
    actionsEs: ["Registrar el resumen del mes", "Publicar para que los residentes lo vean", "Despublicar si es necesario corregirlo"],
    actionsEn: ["Record the month's summary", "Publish it for residents to see", "Unpublish if it needs correcting"],
    tipsEs: ["Los residentes solo ven los resúmenes publicados, nunca los borradores."],
    tipsEn: ["Residents only ever see published summaries, never drafts."],
    roles: ["ADMIN", "BOARD_MEMBER"],
  },
  {
    path: "/resident/dashboard",
    module: "Residente",
    titleEs: "Mi Tablero (Residente)",
    titleEn: "My Dashboard (Resident)",
    purposeEs: "Vista personalizada para titulares: saldo pendiente, sus solicitudes y últimos comunicados.",
    purposeEn: "Personalized owner view: outstanding balance, your requests, and latest announcements.",
    actionsEs: ["Ver saldo pendiente de cuotas", "Acceder rápidamente a permisos y mantenimiento", "Leer últimos comunicados"],
    actionsEn: ["See outstanding fee balance", "Quick access to permits and maintenance", "Read latest announcements"],
    roles: ["RESIDENT", "ADMIN", "BOARD_MEMBER"],
  },
  {
    path: "/resident/fees",
    module: "Residente",
    titleEs: "Mis Cuotas",
    titleEn: "My Fees",
    purposeEs: "Estado de cuenta del residente: cuotas vencidas, pendientes y pagadas con historial.",
    purposeEn: "Resident account statement: overdue, pending, and paid fees with history.",
    actionsEs: ["Ver saldo pendiente total", "Consultar historial de pagos con método y referencia"],
    actionsEn: ["See total outstanding balance", "Review payment history with method and reference"],
    roles: ["RESIDENT", "ADMIN", "BOARD_MEMBER"],
  },
  {
    path: "/resident/finances",
    module: "Residente",
    titleEs: "Mis Finanzas",
    titleEn: "My Finances",
    purposeEs: "Resúmenes financieros mensuales publicados por la administración: ingresos, gastos y fondo de reserva del condominio.",
    purposeEn: "Monthly financial summaries published by administration: the condominium's income, expenses, and reserve fund.",
    actionsEs: ["Ver el resumen financiero de cada mes publicado", "Consultar el desglose de ingresos y gastos"],
    actionsEn: ["View each published month's financial summary", "Review the income and expense breakdown"],
    roles: ["RESIDENT"],
  },
  {
    path: "/resident/works-permit",
    module: "Residente",
    titleEs: "Solicitudes de Titulares",
    titleEn: "Works Permits",
    purposeEs: "Solicitudes de obras de la unidad. Los residentes someten, la Administración y la Junta revisan.",
    purposeEn: "Unit works permit requests. Residents submit, Administration and Board review.",
    actionsEs: ["Solicitar permiso con tipo de obra, fechas y contratista", "Adjuntar planos (URLs)", "Enviar a Resoluciones (Administración y Junta)", "Descargar la resolución (firmada, cuando esté disponible)"],
    actionsEn: ["Submit permit with work type, dates, and contractor", "Attach plans (URLs)", "Send to Resolutions (Administration and Board)", "Download the resolution (signed, once available)"],
    tipsEs: ["Una vez aprobada, el sistema genera un número de resolución secuencial (ej. 2026-001), la registra en Resoluciones y avisa al titular por correo. Cuando se sube la resolución firmada, el titular la descarga desde su solicitud."],
    tipsEn: ["Once approved, the system generates a sequential resolution number (e.g. 2026-001), records it in Resolutions and emails the owner. When the signed resolution is uploaded, the owner downloads it from their request."],
    roles: ["RESIDENT", "ADMIN", "BOARD_MEMBER"],
  },
  {
    path: "/resident/maintenance",
    module: "Residente",
    titleEs: "Mantenimiento",
    titleEn: "Maintenance",
    purposeEs: "Reportes de mantenimiento con categoría, prioridad y fotos. Cronología visual del estado.",
    purposeEn: "Maintenance reports with category, priority, and photos. Visual status timeline.",
    actionsEs: ["Reportar problema con foto", "Seleccionar categoría (Plomería, Eléctrico, etc.) y prioridad", "Ver progreso en la cronología"],
    actionsEn: ["Report issue with photos", "Select category (Plumbing, Electrical, etc.) and priority", "See progress in the timeline"],
    roles: ["RESIDENT", "ADMIN", "BOARD_MEMBER"],
  },
  {
    path: "/resident/announcements",
    module: "Residente",
    titleEs: "Comunicados",
    titleEn: "Announcements",
    purposeEs: "Comunicados oficiales del condominio. Los residentes ven solo los publicados; la Junta puede crear borradores y publicarlos.",
    purposeEn: "Official condominium announcements. Residents see only published; the Board can create drafts and publish them.",
    actionsEs: ["Leer comunicados con categoría y adjuntos", "[Junta/Admin] Crear borrador", "[Junta/Admin] Publicar y enviar por correo a todos los titulares", "[Junta/Admin] Archivar"],
    actionsEn: ["Read announcements with category and attachments", "[Board/Admin] Create draft", "[Board/Admin] Publish and email all owners", "[Board/Admin] Archive"],
    roles: ["RESIDENT", "ADMIN", "BOARD_MEMBER"],
  },
  {
    path: "/documents",
    module: "Documentos",
    titleEs: "Biblioteca de Documentos",
    titleEn: "Document Library",
    purposeEs: "Repositorio centralizado con versionado: Reglamento, Manual de Normas, Contratos, Presupuestos. Los residentes ven solo documentos públicos.",
    purposeEn: "Centralized repository with versioning: Bylaws, Rules Manual, Contracts, Budgets. Residents see only public documents.",
    actionsEs: ["[Junta/Admin] Subir nueva versión de un documento (archiva la anterior automáticamente)", "Filtrar por tipo de documento", "Mostrar todas las versiones (incluyendo archivadas)"],
    actionsEn: ["[Board/Admin] Upload new version (auto-archives previous)", "Filter by document type", "Show all versions (including archived)"],
    roles: ["ADMIN", "BOARD_MEMBER", "RESIDENT"],
  },
  {
    path: "/contracts",
    module: "Legal",
    titleEs: "Contratos",
    titleEn: "Contracts",
    purposeEs: "Tabla de contratos con proveedores con alertas de renovación con código de color.",
    purposeEn: "Table of vendor contracts with color-coded renewal alerts.",
    actionsEs: ["Crear nuevo contrato con PDF adjunto", "Ver días restantes en color (rojo <30d, ámbar <60d, amarillo <90d, verde >90d)"],
    actionsEn: ["Create new contract with attached PDF", "See days remaining in color (red <30d, amber <60d, yellow <90d, green >90d)"],
    tipsEs: ["El sistema envía alertas diarias a la Administración y al Presidente a los 90, 60 y 30 días antes del vencimiento."],
    tipsEn: ["The system sends daily alerts to Administration and the President at 90, 60, and 30 days before expiration."],
    roles: ["ADMIN", "BOARD_MEMBER"],
  },
  {
    path: "/legal-actions",
    module: "Legal",
    titleEs: "Acciones Legales",
    titleEn: "Legal Actions",
    purposeEs: "Tablero kanban de asuntos legales: Abiertas, En Proceso, Resueltas, Cerradas.",
    purposeEn: "Kanban board of legal matters: Open, In Progress, Resolved, Closed.",
    actionsEs: ["Crear nueva acción con unidad relacionada y fecha de vencimiento", "Mover entre columnas haciendo clic en las flechas", "Documentar resolución al cerrar"],
    actionsEn: ["Create new action with related unit and due date", "Move between columns by clicking arrows", "Document resolution when closing"],
    roles: ["ADMIN", "BOARD_MEMBER"],
  },
  {
    path: "/operations-queue",
    module: "Gobierno",
    titleEs: "Cola de Operaciones",
    titleEn: "Operations Queue",
    purposeEs: "Vista consolidada de los 9 tipos de procesos activos del condominio (permisos, mantenimiento, gastos, resoluciones, actas, incidentes, acciones legales, presupuestos y cuotas vencidas), con indicadores de si el trabajo avanza o se está estancando.",
    purposeEn: "Consolidated view of the condominium's 9 active process types (permits, maintenance, expenses, resolutions, minutes, incidents, legal actions, budgets, and overdue fees), with indicators for whether work is moving or stalling.",
    actionsEs: ["Ver totales activos, estancados y completados en los últimos 7 días", "Abrir cualquier tipo de proceso desde su tarjeta", "Filtrar la tabla unificada por tipo o por estado de salud"],
    actionsEn: ["See active, stalled, and last-7-days-completed totals", "Open any process type from its card", "Filter the unified table by type or health status"],
    tipsEs: ["La tabla muestra hasta 10 elementos activos más antiguos por tipo; los totales de las tarjetas son siempre exactos."],
    tipsEn: ["The table shows up to the 10 oldest active items per type; the card totals are always exact."],
    roles: ["ADMIN", "BOARD_MEMBER"],
  },
  {
    path: "/admin/users",
    module: "Admin",
    titleEs: "Usuarios",
    titleEn: "Users",
    purposeEs: "Cuentas del portal: cree usuarios, asigne roles y unidades, y active o desactive el acceso.",
    purposeEn: "Portal accounts: create users, assign roles and units, and activate or deactivate access.",
    actionsEs: ["Crear cuenta (la administración es la única que emite credenciales)", "Asignar rol y unidad", "Desactivar un usuario"],
    actionsEn: ["Create an account (administration is the only source of new credentials)", "Assign role and unit", "Deactivate a user"],
    tipsEs: ["Si no se define una contraseña, el sistema genera una temporal y la envía por correo."],
    tipsEn: ["If no password is set, the system generates a temporary one and emails it."],
    roles: ["ADMIN"],
  },
  {
    path: "/admin/roles",
    module: "Admin",
    titleEs: "Roles y permisos",
    titleEn: "Roles and permissions",
    purposeEs: "Qué ve y qué puede hacer cada rol (Administración, Junta, Titular), los cargos y permisos que se suman al rol, y quién tiene cada uno.",
    purposeEn: "What each role (Administration, Board, Owner) sees and may do, the seats and permissions that add to a role, and who holds each.",
    actionsEs: ["Consultar qué pantallas ve cada rol", "Ver quién es Secretaria, Tesorero o tiene permisos especiales"],
    actionsEn: ["See which screens each role has", "See who is Secretary, Treasurer or holds special permissions"],
    tipsEs: ["Es solo de consulta: el rol, el cargo y los permisos de cada persona se cambian en Usuarios → Editar."],
    tipsEn: ["It is read-only: each person's role, seat and permissions are changed in Users → Edit."],
    roles: ["ADMIN"],
  },
  {
    path: "/admin/units",
    module: "Admin",
    titleEs: "Unidades",
    titleEn: "Units",
    purposeEs: "Catálogo de apartamentos: titular, contacto, edificio, piso, dirección USPS y cuota mensual de mantenimiento.",
    purposeEn: "Catalog of apartments: owner, contact info, building, floor, USPS address and monthly maintenance fee.",
    actionsEs: ["Registrar una nueva unidad", "Editar datos del titular", "Marcar como ocupada o vacía"],
    actionsEn: ["Register a new unit", "Edit owner details", "Mark as occupied or vacant"],
    roles: ["ADMIN"],
  },
  {
    path: "/admin/incidents",
    module: "Seguridad",
    titleEs: "Incidentes",
    titleEn: "Incidents",
    purposeEs: "Registro de incidentes en el condominio: ruido, daño, allanamiento, accidentes, violaciones de reglas.",
    purposeEn: "Registry of incidents in the condominium: noise, damage, trespassing, accidents, rule violations.",
    actionsEs: ["Reportar incidente con tipo, ubicación, fotos", "Actualizar estado (Abierto → Investigando → Resuelto → Cerrado)", "Documentar resolución"],
    actionsEn: ["Report incident with type, location, photos", "Update status (Open → Investigating → Resolved → Closed)", "Document resolution"],
    roles: ["ADMIN", "BOARD_MEMBER"],
  },
  {
    path: "/admin/keys",
    module: "Seguridad",
    titleEs: "Registro de Llaves",
    titleEn: "Key Registry",
    purposeEs: "Inventario de llaves del edificio (Unidad, Área Común, Mantenimiento, Maestra) con historial de entrega y devolución.",
    purposeEn: "Building key inventory (Unit, Common Area, Maintenance, Master) with check-out and check-in history.",
    actionsEs: ["Registrar nueva llave", "Entregar llave a persona (check-out)", "Recibir llave devuelta (check-in)", "Marcar como perdida"],
    actionsEn: ["Register new key", "Check out to a person", "Check in returned key", "Mark as lost"],
    roles: ["ADMIN"],
  },
  {
    path: "/admin/cameras",
    module: "Seguridad",
    titleEs: "Acceso a Cámaras",
    titleEn: "Camera Access",
    purposeEs: "Control de qué usuarios pueden ver qué cámaras del sistema de vigilancia.",
    purposeEn: "Control which users can view which cameras of the surveillance system.",
    actionsEs: ["Otorgar acceso con nivel (Lobby / Áreas Comunes / Todas / Solo Lectura)", "Revocar acceso"],
    actionsEn: ["Grant access with level (Lobby / Common Areas / All / Read-Only)", "Revoke access"],
    roles: ["ADMIN"],
  },
  {
    path: "/admin/staff",
    module: "Personal",
    titleEs: "Personal",
    titleEn: "Staff",
    purposeEs: "Lista de empleados del condominio con su puesto, descripción de funciones, edificios asignados (p. ej. los jardines por edificio), fecha de inicio y estado activo/inactivo.",
    purposeEn: "List of condominium employees with their position, job duties, assigned buildings (e.g. gardens by building), start date, and active/inactive status.",
    actionsEs: ["Agregar nuevo empleado", "Describir sus funciones y asignarle edificios", "Filtrar por edificio", "Desactivar empleado", "Abrir detalle para ver tiempos y tareas"],
    actionsEn: ["Add new employee", "Describe their duties and assign buildings", "Filter by building", "Deactivate employee", "Open detail to see times and tasks"],
    roles: ["ADMIN"],
  },
  {
    path: "/admin/staff/:id",
    module: "Personal",
    titleEs: "Detalle de Empleado",
    titleEn: "Employee Detail",
    purposeEs: "Hoja de tiempo (entrada/salida con cálculo automático de horas) y tablero kanban de tareas (Pendiente / En Proceso / Completada).",
    purposeEn: "Timesheet (clock-in/clock-out with automatic hour calculation) and kanban task board (Pending / In Progress / Done).",
    actionsEs: ["Registrar entrada y salida", "Cerrar entrada abierta con 'Cerrar ahora'", "Crear tarea con prioridad y vencimiento", "Mover tareas entre columnas"],
    actionsEn: ["Log clock-in and clock-out", "Close an open entry with 'Close now'", "Create task with priority and due date", "Move tasks between columns"],
    roles: ["ADMIN"],
  },
  {
    path: "/admin/audit-log",
    module: "Admin",
    titleEs: "Registro de Auditoría",
    titleEn: "Audit Log",
    purposeEs: "Historial completo de todas las acciones realizadas en el sistema, con filtros por usuario, acción y entidad.",
    purposeEn: "Complete history of all actions performed in the system, with filters by user, action, and entity.",
    actionsEs: ["Filtrar por acción (ej. USER_LOGIN, FEE_PAYMENT_RECORDED)", "Filtrar por tipo de entidad", "Paginar resultados"],
    actionsEn: ["Filter by action (e.g. USER_LOGIN, FEE_PAYMENT_RECORDED)", "Filter by entity type", "Paginate results"],
    tipsEs: ["Cada acción incluye usuario, IP, timestamp y metadatos JSON con detalles."],
    tipsEn: ["Each action includes user, IP, timestamp, and JSON metadata with details."],
    roles: ["ADMIN"],
  },
];
